'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  Package,
  Truck,
  Calculator,
  Send,
  CheckCircle2,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  Flame,
  Award,
  Clock,
  Boxes,
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useCommerce } from '@/context/CommerceContext';

interface BulkPackage {
  id: string;
  title: string;
  category: string;
  subtitle: string;
  badge: string;
  minOrderQty: number;
  basePrice: number;
  image: string;
  includedSpecs: string[];
  packagingType: string;
}

export default function BulkOrdersPage() {
  const { openRfqWithData, products } = useCommerce();

  const bulkPackages: BulkPackage[] = React.useMemo(() => {
    if (!products || products.length === 0) return [];
    return products.slice(0, 6).map((p) => {
      const v1kg = p.variants?.find((v) => v.weight === '1kg') || p.variants?.[0] || { price: 450 };
      const tenKgPrice = Math.round(v1kg.price * 10 * 0.85); // 15% wholesale discount
      return {
        id: p.id,
        title: `${p.name} Master Catering Tub`,
        category: typeof p.category === 'string' ? p.category : 'Wedding & Temple Catering',
        subtitle: p.shortDesc || p.description?.slice(0, 100) || 'Traditional double-twisted crunchy delicacy in cold-pressed oil.',
        badge: p.isBestseller ? 'Catering Bestseller' : 'Direct Wholesale',
        minOrderQty: 10,
        basePrice: tenKgPrice,
        image: p.image || '/images/cat_kai_murukku.jpg',
        includedSpecs: [
          '100% Pure Cold-Pressed Oil Frying',
          'Aseptic moisture-lock catering container',
          'Fresh daily preparation on confirmed bookings',
          'Custom 250g / 500g inner portion bags available',
        ],
        packagingType: '10kg / 25kg Food-Grade Master Sealed Tub',
      };
    });
  }, [products]);

  const [selectedProduct, setSelectedProduct] = useState('');
  const [targetWeightKg, setTargetWeightKg] = useState(25);
  const [destinationCity, setDestinationCity] = useState('');
  const [clientName, setClientName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  React.useEffect(() => {
    if (products.length > 0 && !selectedProduct) {
      setSelectedProduct(products[0].id);
    }
  }, [products, selectedProduct]);

  // Price estimate calculation
  const productObj = products.find((p) => p.id === selectedProduct) || products[0];
  const baseKgPrice = productObj?.variants?.find((v) => v.weight === '1kg')?.price || productObj?.variants?.[0]?.price || 600;

  let discountPercent = 0;
  if (targetWeightKg >= 100) discountPercent = 30;
  else if (targetWeightKg >= 50) discountPercent = 25;
  else if (targetWeightKg >= 25) discountPercent = 18;
  else if (targetWeightKg >= 10) discountPercent = 10;

  const rawTotal = baseKgPrice * targetWeightKg;
  const discountAmount = Math.round(rawTotal * (discountPercent / 100));
  const estimatedTotal = rawTotal - discountAmount;

  const handleInquire = (bulkPack: BulkPackage) => {
    openRfqWithData({
      clientName: clientName || 'Bulk Procurement Client',
      occasion: bulkPack.category,
      packageTitle: bulkPack.title,
      orderType: 'BULK',
      estimatedBoxes: bulkPack.minOrderQty,
      notes: `Inquiring about ${bulkPack.title} (${bulkPack.packagingType}) with destination delivery to ${destinationCity || 'India'}.`,
    });
  };

  const handleCalculateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!productObj) return;
    openRfqWithData({
      clientName,
      phone,
      email,
      destinationCity,
      orderType: 'BULK',
      items: [
        {
          name: productObj.name,
          variant: `${targetWeightKg} kg`,
          quantity: 1,
          unitPrice: estimatedTotal,
          total: estimatedTotal,
        },
      ],
      notes: `Bulk procurement calculation for ${targetWeightKg}kg of ${productObj.name} to ${destinationCity}`,
    });
    setSubmitted(true);
  };

  const wholesaleCapabilities = [
    {
      icon: Flame,
      title: 'Wood-Fired Iron Kadai Frying',
      subtitle: 'Pure Cold-Pressed Oils',
      desc: 'Cooked fresh on order in traditional vats for authentic crunch with zero palm oil.',
    },
    {
      icon: Truck,
      title: 'Direct Freight Logistics',
      subtitle: 'Pan-India Synchronized Transport',
      desc: 'Dedicated transport pass and palletized cargo dispatch to event halls and warehouses.',
    },
    {
      icon: Boxes,
      title: 'Tier-Based Volume Savings',
      subtitle: '10% to 30% Wholesale Discount',
      desc: 'Transparent pricing with automatic tiered savings applied on orders over 10kg.',
    },
    {
      icon: ShieldCheck,
      title: 'FSSAI & GST Certified',
      subtitle: 'Commercial Food Compliance',
      desc: '100% tax-compliant B2B invoicing with HSN coding and food safety certifications.',
    },
  ];

  return (
    <main className="min-h-screen flex flex-col bg-white">
      <Header />

      {/* 1. Hero Section (Clean, refined aesthetic matching Occasions page) */}
      <section className="bg-[#FAFBF9] border-b border-neutral-200 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <div className="inline-flex items-center space-x-1.5 text-xs font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100/70 px-3.5 py-1 rounded-full">
            <Package className="w-3.5 h-3.5 text-emerald-700" />
            <span>Commercial Wholesale & Catering Supply</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-semibold text-neutral-900 uppercase tracking-tight">
            AUTHENTIC WHOLESALE BULK SAVOURIES & SWEETS
          </h1>

          <p className="text-sm sm:text-base text-neutral-600 max-w-2xl mx-auto font-medium leading-relaxed">
            Direct kitchen-to-venue wholesale supply for caterers, event organizers, wedding families,
            and institutional distributors. Freshly wood-fired in pure cold-pressed oil with transparent volume tiers.
          </p>

          <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
            <a
              href="#bulk-packages"
              className="px-6 py-3 rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-bold text-xs sm:text-sm uppercase tracking-wider transition-all shadow-md"
            >
              Browse Wholesale Packs
            </a>
            <a
              href="#calculator-section"
              className="px-6 py-3 rounded-full bg-white border border-neutral-300 hover:border-emerald-700 text-neutral-800 hover:text-emerald-800 font-bold text-xs sm:text-sm uppercase tracking-wider transition-all"
            >
              Instant Cost Calculator
            </a>
          </div>
        </div>
      </section>

      {/* 2. Wholesale Capabilities Grid */}
      <section className="py-12 bg-white border-b border-neutral-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {wholesaleCapabilities.map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={i}
                  className="p-5 rounded-2xl bg-[#FAFBF9] border border-neutral-200/80 hover:border-emerald-600/50 hover:shadow-md transition-all space-y-2"
                >
                  <div className="w-9 h-9 rounded-xl bg-emerald-100/70 text-emerald-800 flex items-center justify-center mb-2">
                    <Icon className="w-4 h-4 text-emerald-800" />
                  </div>
                  <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider block">
                    {c.subtitle}
                  </span>
                  <h3 className="text-base font-bold text-neutral-900">
                    {c.title}
                  </h3>
                  <p className="text-xs text-neutral-600 leading-relaxed font-medium">
                    {c.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3. Pre-Curated Bulk Procurement Packages (Horizontal Cards with Details on Left, Image on Right) */}
      <section id="bulk-packages" className="py-14 bg-white flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-neutral-900 uppercase tracking-tight">
                PRE-CURATED WHOLESALE BULK PACKS
              </h2>
              <div className="h-0.5 w-12 bg-emerald-600 rounded-full mt-1.5"></div>
            </div>
            <a
              href="#calculator-section"
              className="text-xs sm:text-sm font-bold text-emerald-700 hover:underline"
            >
              Custom Quantity Calculator →
            </a>
          </div>

          {bulkPackages.length === 0 ? (
            <div className="text-center py-16 bg-[#FAFBF9] rounded-3xl border border-neutral-200/80 p-8 space-y-4">
              <Package className="w-10 h-10 text-emerald-700 mx-auto" />
              <h3 className="text-lg font-bold text-neutral-800">No bulk wholesale packs currently available in catalogue</h3>
              <p className="text-xs text-neutral-500 max-w-md mx-auto">
                Connect directly with our institutional sales desk to arrange bespoke kitchen-to-venue catering.
              </p>
              <Link
                href="/contact"
                className="inline-block px-5 py-2.5 rounded-full bg-emerald-800 text-white text-xs font-bold uppercase tracking-wider"
              >
                Contact Wholesale Desk
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {bulkPackages.map((bp) => (
                <div
                  key={bp.id}
                  className="flex flex-col-reverse sm:flex-row items-stretch justify-between rounded-3xl bg-white border border-neutral-200/80 overflow-hidden hover:shadow-xl hover:border-emerald-600/40 transition-all duration-300 group"
                >
                  {/* LEFT SIDE: Details */}
                  <div className="flex-1 p-6 sm:p-7 flex flex-col justify-between space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
                          {bp.category}
                        </span>
                      </div>

                      <h3 className="text-lg sm:text-xl font-semibold text-neutral-900 leading-snug">
                        {bp.title}
                      </h3>

                      <p className="text-xs text-neutral-600 mt-2 leading-relaxed font-medium">
                        {bp.subtitle}
                      </p>
                    </div>

                    <div className="space-y-1.5 py-3 border-t border-b border-neutral-100">
                      <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider block">
                        Commercial Specifications:
                      </span>
                      <ul className="space-y-1.5 text-xs text-neutral-700 font-medium">
                        {bp.includedSpecs.map((s, i) => (
                          <li key={i} className="flex items-center space-x-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                            <span className="line-clamp-1">{s}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="pt-1 flex items-center justify-between gap-3">
                      <div>
                        <span className="text-lg font-bold text-neutral-900">From ₹{bp.basePrice}</span>
                        <span className="text-[10px] text-neutral-400 block font-medium">Min. {bp.minOrderQty} kg</span>
                      </div>

                      <button
                        onClick={() => handleInquire(bp)}
                        className="py-2.5 px-4 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold uppercase tracking-wider transition-colors cursor-pointer shadow-xs whitespace-nowrap"
                      >
                        Inquire / Quote
                      </button>
                    </div>
                  </div>

                  {/* RIGHT SIDE: Image */}
                  <div className="relative w-full sm:w-[42%] md:w-[44%] min-h-[260px] sm:min-h-[340px] bg-neutral-100 overflow-hidden shrink-0">
                    <Image
                      src={bp.image || '/images/royal_gift_box.jpg'}
                      alt={bp.title || 'Bulk Hamper Package'}
                      fill
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 30vw"
                      unoptimized
                      className="object-cover object-center group-hover:scale-105 transition-transform duration-500"
                    />
                    <span className="absolute top-3.5 right-3.5 text-[10px] sm:text-[11px] font-bold text-white bg-emerald-800/90 backdrop-blur-xs px-3 py-1 rounded-full uppercase tracking-wider shadow-sm">
                      {bp.badge}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 4. Interactive Volume Tiers & Calculator Section */}
      <section id="calculator-section" className="py-14 bg-[#FAFBF9] border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="text-center space-y-2">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-700">
              TRANSPARENT COMMERCIAL SAVINGS
            </span>
            <h3 className="text-2xl sm:text-3xl font-semibold text-neutral-900 uppercase tracking-tight">
              VOLUME PRICING CALCULATOR
            </h3>
            <p className="text-xs sm:text-sm text-neutral-500 max-w-lg mx-auto font-medium">
              Calculate live wholesale pricing with tiered discounts and instant RFQ dispatch.
            </p>
          </div>

          {/* Volume Tiers Badges */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { tier: 'Tier 1', qty: '10 – 24 KG', discount: '10% OFF', for: 'Family Pujas & Small Events' },
              { tier: 'Tier 2', qty: '25 – 49 KG', discount: '18% OFF', for: 'Engagements & Banquets' },
              { tier: 'Tier 3', qty: '50 – 99 KG', discount: '25% OFF', for: 'Grand Weddings & Caterers' },
              { tier: 'Tier 4', qty: '100+ KG', discount: '30% OFF', for: 'Distributors & Institutions' },
            ].map((t, idx) => (
              <div key={idx} className="p-4 rounded-2xl bg-white border border-neutral-200 text-center space-y-1 shadow-2xs">
                <span className="text-[10px] font-bold text-neutral-400 uppercase tracking-wider">{t.tier}</span>
                <div className="text-base sm:text-lg font-bold text-neutral-900">{t.qty}</div>
                <div className="text-xs font-extrabold text-emerald-700">{t.discount}</div>
                <div className="text-[10px] text-neutral-500">{t.for}</div>
              </div>
            ))}
          </div>

          {/* Interactive Calculator Box */}
          <div className="max-w-3xl mx-auto bg-white p-6 sm:p-8 rounded-3xl border border-neutral-200 shadow-md">
            {submitted ? (
              <div className="p-6 rounded-2xl bg-emerald-50 border border-emerald-200 text-center space-y-2">
                <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
                <h4 className="text-base font-bold text-emerald-900">Wholesale Quotation Request Sent!</h4>
                <p className="text-xs text-emerald-700">
                  Our commercial sales manager will contact you with exact batch availability, logistics freight, and pro-forma invoice.
                </p>
              </div>
            ) : (
              <form onSubmit={handleCalculateSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Select Snack / Sweet *</label>
                    <select
                      value={selectedProduct}
                      onChange={(e) => setSelectedProduct(e.target.value)}
                      disabled={products.length === 0}
                      className="w-full px-3.5 py-2.5 text-xs bg-neutral-50 border border-neutral-200 rounded-xl focus:outline-none focus:border-emerald-600 font-semibold cursor-pointer disabled:opacity-50"
                    >
                      {products.length === 0 ? (
                        <option value="">No products available in catalog</option>
                      ) : (
                        products.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Required Quantity (Kilograms) *</label>
                    <input
                      type="number"
                      min="10"
                      step="5"
                      value={targetWeightKg}
                      onChange={(e) => setTargetWeightKg(parseInt(e.target.value, 10) || 10)}
                      className="w-full px-3.5 py-2.5 text-xs bg-neutral-50 border border-neutral-200 rounded-xl focus:outline-none focus:border-emerald-600 font-semibold"
                    />
                  </div>
                </div>

                {/* Live Cost Estimate Callout */}
                <div className="p-4 rounded-2xl bg-emerald-50/70 border border-emerald-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
                  <div>
                    <span className="text-[11px] font-bold text-emerald-800 uppercase tracking-wider block">
                      Estimated Wholesale Price:
                    </span>
                    <div className="flex items-baseline space-x-2">
                      <span className="text-2xl font-bold text-emerald-950">₹{estimatedTotal.toLocaleString('en-IN')}</span>
                      {discountPercent > 0 && (
                        <span className="text-xs text-neutral-400 line-through">₹{rawTotal.toLocaleString('en-IN')}</span>
                      )}
                      <span className="text-xs text-emerald-700 font-bold">({discountPercent}% Tier Applied)</span>
                    </div>
                  </div>

                  <span className="text-xs text-neutral-500 font-medium">
                    ₹{(Math.round(estimatedTotal / targetWeightKg)).toLocaleString('en-IN')} per kg
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Contact Name *</label>
                    <input
                      type="text"
                      required
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      placeholder="Your name / Caterer"
                      className="w-full px-3.5 py-2 text-xs bg-neutral-50 border border-neutral-200 rounded-xl focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Mobile / WhatsApp *</label>
                    <input
                      type="tel"
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+91 98765 43210"
                      className="w-full px-3.5 py-2 text-xs bg-neutral-50 border border-neutral-200 rounded-xl focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-neutral-700 uppercase mb-1">Destination City *</label>
                    <input
                      type="text"
                      required
                      value={destinationCity}
                      onChange={(e) => setDestinationCity(e.target.value)}
                      placeholder="e.g. Chennai, Bangalore, Madurai"
                      className="w-full px-3.5 py-2 text-xs bg-neutral-50 border border-neutral-200 rounded-xl focus:outline-none focus:border-emerald-600"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full py-3.5 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white font-bold text-xs uppercase tracking-wider shadow-md transition-all cursor-pointer flex items-center justify-center space-x-2"
                >
                  <Send className="w-4 h-4" />
                  <span>Request Official Commercial Quotation</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* 5. Bottom Wholesale Catering CTA Banner */}
      <section className="bg-[#0A261A] text-white py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h2 className="text-2xl sm:text-4xl font-semibold uppercase tracking-tight">
            Planning a Wedding or Commercial Food Distribution?
          </h2>
          <p className="text-xs sm:text-sm text-neutral-300 max-w-xl mx-auto font-medium leading-relaxed">
            Speak directly with our Master Kitchen Operations Head for custom taste recipes, trial batches, and priority logistics.
          </p>
          <div className="pt-2 flex flex-wrap items-center justify-center gap-4">
            <a
              href="https://wa.me/919443210890?text=Hi%2C%20I%20would%20like%20to%20inquire%20about%20Wholesale%20Bulk%20Snack%20Orders"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 rounded-full bg-[#F5B52E] hover:bg-[#ffc34d] text-[#0A261A] font-bold text-xs sm:text-sm uppercase tracking-wider transition-all"
            >
              WhatsApp Wholesale Desk
            </a>
            <Link
              href="/contact"
              className="px-6 py-3 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold text-xs sm:text-sm uppercase tracking-wider transition-all"
            >
              Contact Kitchen Office
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
